import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CommonService } from '../../../../services/common/common.service';
import { VideosDataService, VideosColumnsService } from '../../../services';
import { Video, VideosResult } from '../../../interfaces';
import { DialogFilerComponent } from '../../filer/filer.component';

@Component({
  selector: 'app-video-form',
  templateUrl: './video-form.component.html',
  styleUrls: ['./video-form.component.scss'],
  providers: [MessageService, ConfirmationService, DynamicDialogRef, DynamicDialogConfig],
})
export class VideoFormComponent implements OnInit, OnDestroy, AfterViewInit {

  public video: Video = {};
  public videoForm: FormGroup = new FormGroup({});
  public videos: Video[] = [];
  public defaultParameters: { [param: string]: string | string[] } = { offset: '0', limit: '1000' };
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '1000' };
  public isLoading: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  public redirect = true;
  public disableSubmit = false;
  public enableFormAlert = true;

  private filerDialog: DynamicDialogRef = new DynamicDialogRef();

  constructor(
    private translate: TranslateService,
    public common: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private videosDataService: VideosDataService,
    public videosColumnsService: VideosColumnsService,
  ) {
  }

  ngOnInit(): void {
    console.log('ngOnInit', 'VideoFormComponent');
    this.route.params.subscribe((params: any) => {
      if (typeof params['id'] !== 'undefined') {
        this.titleService.setTitle('Video - Edit');
        this.getVideo(parseInt(params['id'], 10));
      }
    });
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.video !== 'undefined') {
      this.video = this.dynamicDialogConfig.data.video;
    }
    if (typeof this.video.id === 'undefined' || this.video.id === null) {
      this.video.id = null;
      if (typeof this.videosColumnsService.videoTypes[0] !== 'undefined') {
        if (this.videosColumnsService.videoTypes[0].id !== null) {
          this.video.videoType = this.videosColumnsService.videoTypes[0].id;
        }
      }
      if (typeof this.videosColumnsService.prices[0] !== 'undefined') {
        if (this.videosColumnsService.prices[0].id !== null) {
          this.video.price = this.videosColumnsService.prices[0].id;
        }
      }
      if (typeof this.videosColumnsService.sharings[0] !== 'undefined') {
        if (this.videosColumnsService.sharings[0].id !== null) {
          this.video.sharing = this.videosColumnsService.sharings[0].id;
        }
      }
      this.video.remoteSource = false;
      this.video.isTop = false;
      this.video.isNew = false;
      this.video.enableStreaming = false;
      this.video.autoPublishing = false;
      this.video.startPublishing = moment().set({ minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD\THH:mm:ssZ');
      this.video.endPublishing = moment().set({ minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD\THH:mm:ssZ');
      this.video.publishDate = moment().set({ minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD\THH:mm:ssZ');

    }
    this.getVideos();
    this.prepareFrom();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy', 'VideoFormComponent');
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit', 'VideoFormComponent');
  }

  getVideo(id: number): void {
    this.isLoading = true;
    this.videosDataService.getVideo(id).subscribe((response: Video | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.video = response;
        this.prepareFrom();
      }
    });
  }

  getVideos(): void {
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.videos !== 'undefined' && this.dynamicDialogConfig.data.videos.length > 0) {
      this.videos = this.dynamicDialogConfig.data.videos;
      return;
    }
    this.videosDataService.getVideos(this.parameters).subscribe((response: VideosResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.videos = response.results;
      }
    });
  }

  prepareFrom(): void {
    this.video.startPublishing = moment(this.video.startPublishing).toDate();
    this.video.endPublishing = moment(this.video.endPublishing).toDate();
    this.video.publishDate = moment(this.video.publishDate).toDate();
    this.videoForm = new FormGroup({
      show: new FormControl((typeof this.video.show === 'number') ? this.video.show : this.video.show?.id),
      videoType: new FormControl((typeof this.video.videoType === 'number') ? this.video.videoType : this.video.videoType?.id),
      price: new FormControl((typeof this.video.price === 'number') ? this.video.price : this.video.price?.id),
      sharing: new FormControl((typeof this.video.sharing === 'number') ? this.video.sharing : this.video.sharing?.id),
      name: new FormControl(this.video.name, [Validators.required,]),
      nameAr: new FormControl(this.video.nameAr, []),
      nameFr: new FormControl(this.video.nameFr, []),
      duration: new FormControl(this.video.duration, []),
      picture: new FormControl(this.video.picture, []),
      watermarkText: new FormControl(this.video.watermarkText, []),
      file: new FormControl(this.video.file, []),
      // fileAr: new FormControl(this.video.fileAr, []),
      fileSize: new FormControl(this.video.fileSize, []),
      description: new FormControl(this.video.description, []),
      descriptionAr: new FormControl(this.video.descriptionAr, []),
      descriptionFr: new FormControl(this.video.descriptionFr, []),
      enableStreaming: new FormControl(this.video.enableStreaming, []),
      seasonNumber: new FormControl(this.video.seasonNumber, []),
      episodeNumber: new FormControl(this.video.episodeNumber, []),
      isTop: new FormControl(this.video.isTop, [Validators.required,]),
      isNew: new FormControl(this.video.isNew, [Validators.required,]),
      status: new FormControl(this.video.status, [Validators.required,]),
      autoPublishing: new FormControl(this.video.autoPublishing, []),
      startPublishing: new FormControl(this.video.startPublishing, []),
      endPublishing: new FormControl(this.video.endPublishing, []),
      totalPreviewed: new FormControl(this.video.totalPreviewed, []),
      totalDownloads: new FormControl(this.video.totalDownloads, []),
      totalHits: new FormControl(this.video.totalHits, []),
      totalComments: new FormControl(this.video.totalComments, []),
      totalRatings: new FormControl(this.video.totalRatings, []),
      averageRatings: new FormControl(this.video.averageRatings, []),
      totalLikes: new FormControl(this.video.totalLikes, []),
      totalDislikes: new FormControl(this.video.totalDislikes, []),
      totalBookmarks: new FormControl(this.video.totalBookmarks, []),
      slug: new FormControl(this.video.slug, []),
      slugAr: new FormControl(this.video.slugAr, []),
      slugFr: new FormControl(this.video.slugFr, []),
      remoteSource: new FormControl(this.video.remoteSource, []),
      url: new FormControl(this.video.url, []),
      alternativeUrl: new FormControl(this.video.alternativeUrl, []),
      copyright: new FormControl(this.video.copyright, []),
      id: new FormControl(this.video.id, []),
      category: new FormControl((typeof this.video.category === 'number') ? this.video.category : this.video.category?.id, [Validators.required,]),
      subCategory: new FormControl((typeof this.video.subCategory === 'number') ? this.video.subCategory : this.video.subCategory?.id, []),
      experts: new FormControl((typeof this.video.experts === 'number') ? this.video.experts : this.video.experts?.id, []),
      isHeadline: new FormControl(this.video.isHeadline, [Validators.required,]),
      publishDate: new FormControl(this.video.publishDate, [Validators.required,]),

    });
  }

  list() {
    this.router.navigate(['/videos']);
  }

  showErrors() {
    this.showErrors();
    const invalidFields: string[] = [];
    Object.keys(this.videoForm.controls).forEach((field: any) => {
      const control = this.videoForm.get(field);
      if (control !== null) {
        control.markAsTouched({ onlySelf: true });
        if (control.invalid) {
          invalidFields.push(field);
        }
      }
    });
  }

  submitForm(redirect: boolean) {
    console.log('this.videoForm', this.videoForm)
    this.redirect = redirect;
    if (!this.videoForm.valid) {
      this.showErrors();
    } else {
      this.saveData(redirect);
    }
  }

  saveData(redirect: boolean) {
    this.video = this.videoForm.value;
    this.disableSubmit = true;
    console.log('video', this.video);
    if (typeof this.video.id !== 'undefined' && this.video.id !== null) {
      this.add(redirect);
    } else {
      this.update(redirect);
    }
    return false;
  }

  add(redirect: boolean) {
    this.videosDataService.updateVideo(this.video).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('VIDEOUPDATED') });
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({ refresh: true });
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEONOTUPDATED') + ' ' + error.message });
      console.warn(error);
    });
  }

  update(redirect: boolean) {
    this.videosDataService.addVideo(this.video).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('VIDEOCREATED') });
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({ refresh: true });
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEONOTCREATED') + ' ' + error.message });
      console.warn(error);
    });
  }

  openFilerDialog(field: string): void {
    let url = '';
    const control = this.videoForm.get(field);
    if (control !== null) {
      url = control.value;
    }
    this.filerDialog = this.dialogService.open(DialogFilerComponent, {
      header: '', // this.translate.instant('FILEMANAGER'),
      width: '90%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1500,
      data: {
        path: 'videos',
        field: field,
        url: url,
      },
    });
    this.filerDialog.onClose.subscribe((response: any) => {
      if (response && response.selected) {
        const control = this.videoForm.get(field);
        if (control !== null) {
          control.setValue(response.selected.webPath);
        }
      }
    });
  }
  openFilerDialogAr(field: string): void {
    let url = '';
    const control = this.videoForm.get(field);
    if (control !== null) {
      url = control.value;
    }
    this.filerDialog = this.dialogService.open(DialogFilerComponent, {
      header: '', // this.translate.instant('FILEMANAGER'),
      width: '90%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1500,
      data: {
        path: 'videos',
        field: field,
        url: url,
      },
    });
    this.filerDialog.onClose.subscribe((response: any) => {
      if (response && response.selected) {
        const control = this.videoForm.get(field);
        if (control !== null) {
          control.setValue(response.selected.webPath);
        }
      }
    });
  }
}

@Component({
  selector: 'app-video-form',
  templateUrl: './video-form.component.html',
  styleUrls: ['./video-form.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class DialogVideoFormComponent extends VideoFormComponent {
}

